<template>
  <div class="gjw-table">
    <el-table
      v-loading="loading"
      :data="tableList"
      style="width: 100%"
      :default-sort="defaultSort"
      :highlight-current-row="highlightCurrentRow"
      :row-class-name="rowClassName"
      v-bind="$attrs.tableAttrs || $attrs['table-attrs'] || {}"
      v-on="$listeners"
      @selection-change="selectionChange"
      :header-cell-style="{'background': '#F0F0F0'}"
      :max-height="maxHeight"
    >
      <template #empty>
        <empty-content text="暂无内容"/>
      </template>
      <template v-for="(column, index) in columns">
        <!--多选/序号/展开按钮列-->
        <el-table-column
          v-if="['selection', 'index', 'expand'].includes(column.type)"
          :key="index"
          :type="column.type"
          :label="column.label"
          :width="column.width"
          v-bind="column.attrs || {}"
          :selectable="(row, index) => !column.selectable || column.selectable(row, index)"
        >
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
          <template v-if="column.type === 'expand'" v-slot="scope">
            <slot name="expand" :row="scope.row" :index="scope.$index" />
          </template>
        </el-table-column>
        <!--图片列-->
        <el-table-column v-if="column.type === 'image'" :key="index" :label="column.label" :prop="column.prop" :formatter="column.formatter" :width="column.width" :min-width="column.minWidth" v-bind="column.attrs || {}">
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
          <template v-slot="scope">
            <img :src="column.fmtUrl ? column.fmtUrl(scope.row[column.prop]) : scope.row[column.prop]" height="40" />
          </template>
        </el-table-column>
        <!--具名插槽-->
        <el-table-column v-if="column.type === 'slot'" :key="index" :label="column.label" :prop="column.prop" :formatter="column.formatter" :sortable="column.sortable" :width="column.width" :min-width="column.minWidth" v-bind="column.attrs || {}" :show-overflow-tooltip="column.showOverflowTooltip!==false">
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
          <template v-slot="scope">
            <slot :name="column.slotName" :row="scope.row" :index="scope.$index" />
          </template>
        </el-table-column>
        <!--普通展示列-->
        <el-table-column v-else-if="!column.type && !column.editType" :key="index" :label="column.label" :prop="column.prop" :formatter="column.formatter" :sortable="column.sortable" :width="column.width" :min-width="column.minWidth" :show-overflow-tooltip="column.showOverflowTooltip!==false" v-bind="column.attrs || {}">
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
        </el-table-column>
        <!--普通展示列，支持行内编辑-->
        <el-table-column v-else-if="!column.type && column.editType" :key="index" :label="column.label" :prop="column.prop" :sortable="column.sortable" :width="column.width" :min-width="column.minWidth" v-bind="column.attrs || {}">
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
          <template v-slot="scope">
            <gjw-table-edit-cell
              v-if="editIndex === scope.$index"
              :key="column.prop"
              :type="column.editType"
              :col-prop="column.prop"
              :init-value="scope.row[column.prop]"
              :config="column.editConfig"
              @value-change="tableCellChange"
            />
            <span v-else>{{ column.formatter ? column.formatter(scope.row, scope.column, scope.row[column.prop]) : scope.row[column.prop] }}</span>
          </template>
        </el-table-column>
        <!--操作列-->
        <el-table-column v-else-if="column.type === 'operate'" :key="index" :label="column.label" :width="column.width" :min-width="column.minWidth" v-bind="column.attrs || {}">
          <template v-if="column.labelSlotName" #header>
            <slot :name="column.labelSlotName" />
          </template>
          <template v-slot="scope">
            <div v-if="editIndex === scope.$index">
              <el-button type="text">
                <i class="el-icon-check green" style="font-size:16px" @click.stop="editConfirm(scope.row)" />
              </el-button>
              <el-button type="text">
                <i class="el-icon-close red" style="font-size:16px" @click.stop="editCancel" />
              </el-button>
            </div>
            <!--其他按钮-->
            <gjw-table-operate v-else :buttons="column.btns" :row="scope.row" :row-index="scope.$index" />
          </template>
        </el-table-column>
      </template>
    </el-table>
    <gjw-pagination
      v-if="hasPage"
      v-bind="$attrs.paginationAttrs || $attrs['pagination-attrs'] || {}"
      :total="dataTotal"
      :current-page="pageNo"
      :page-size="pageSize"
      @change="pageChange"
    />
  </div>
</template>

<script>
import Utils from '../../utils'
import GjwTableOperate from './GjwTableOperate'
import GjwTableEditCell from './GjwTableEditCell'
import GjwPagination from '../../gjwPagination/src/GjwPagination'
import EmptyContent from '@/components/layout/EmptyContent'

export default {
  name: 'GjwTable',
  components: { GjwTableOperate, GjwTableEditCell, GjwPagination, EmptyContent },
  inheritAttrs: false,
  props: {
    // 获取列表数据接口
    requestFn: {
      type: Function
    },
    // 处理表格行数据的方法
    handleTableDataFn: {
      type: Function
    },
    responseConfig: {
      type: Object,
      default() {
        return {}
      },
    },
    // 列表数据源，优先级比requestFn低
    listData: {
      type: Array
    },
    // type, label, prop, formatter, width
    columns: {
      type: Array,
    },
    searchData: {
      type: Object,
      default() {
        return {}
      }
    },
    // 默认排序,{ prop: '', order: '' }
    defaultSort: {
      type: Object,
      default() {
        return {}
      }
    },
    // 排序字段的对应关系(有requestFn时，需要传该属性)
    sortMap: {
      type: Object,
      default() {
        return {}
      }
    },
    // 是否显示页码
    hasPage: {
      type: Boolean,
      default: true,
    },
    totalCount: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    size: {
      type: Number,
      default: 10
    },
    maxHeight: {
      type: [String, Number],
      default: '100%'
    },
    highlightCurrentRow: {
      type: Boolean,
      default: false
    },
    rowClassName: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      searchOrder: {},
      tableList: [],
      selectList: [],
      loading: false,
      dataTotal: 0, // 当hasPage为true、requestFn没传的时候，dataTotal为0
      pageNo: this.currentPage,
      pageSize: this.size || 10,
      tempRow: {},
      editIndex: -1,
    }
  },
  computed: {
    listResConfig() {
      const defaultConfig = {
        isDirectData: true,
        tableDataKey: 'list',
        dataTotalKey: 'totalCount'
      }
      return { ...defaultConfig, ...this.responseConfig }
    },
  },
  watch: {
    listData: {
      handler(newVal) {
        this.tableList = newVal || []
      },
      immediate: true
    },
    defaultSort: {
      handler(newVal) {
        const searchOrder = Utils.handleTableSort(newVal, this.sortMap)
        this.searchOrder = {
          sort_field: searchOrder.sortField,
          order_type: searchOrder.orderType,
        }
      },
      immediate: true
    },
    totalCount: {
      handler(newVal) {
        this.dataTotal = newVal || 0
      },
      immediate: true
    },
  },
  created() {
    this.getList()
  },
  methods: {
    refresh(query) {
      if (this.editIndex !== -1) {
        this.editCancel()
      }
      if (!this.requestFn) { return }
      this.pageNo = 1
      this.getList(query)
    },
    getList(query) {
      if (this.requestFn) {
        const searchObj = query || this.searchData
        const pages = this.hasPage ? { page_no: this.pageNo, page_size: this.pageSize } : {}
        const params = {
          ...searchObj,
          ...this.searchOrder,
          ...pages,
        }
        this.loading = true
        this.requestFn(params).then((res) => {
          const config = this.listResConfig || {}
          const { tableDataKey, dataTotalKey } = config
          const data = (config.isDirectData ? res : res.data) || {}
          this.tableList = data[tableDataKey] || []
          // 处理表格数据
          if (this.handleTableDataFn) {
            this.tableList = this.handleTableDataFn(this.tableList)
          }
          this.dataTotal = data[dataTotalKey] || 0
          this.$emit('list-change', this.tableList, this.dataTotal)
        }).finally(() => {
          this.loading = false
        })
      }
    },
    pageChange(payload) {
      this.pageNo = payload.pageNo
      this.pageSize = payload.pageSize
      this.getList()
      this.$emit('page-change', payload)
    },
    editRow(row, rowIndex) {
      this.tempRow = { ...row }
      this.editIndex = rowIndex
    },
    editCancel() {
      this.tempRow = {}
      this.editIndex = -1
    },
    editConfirm(row) {
      if (Utils.diffObjValue(row, this.tempRow)) {
        this.$emit('handle-edit', this.tempRow)
      } else {
        this.editCancel()
      }
    },
    // 多选选项变化
    selectionChange(selection) {
      this.selectList = selection
      this.$emit('table-selection-change', selection)
    },
    // 排序变化
    sortChange(sortObj) {
      const searchOrder = Utils.handleTableSort(sortObj, this.sortMap)
      this.searchOrder = {
        sort_field: searchOrder.sortField,
        order_type: searchOrder.orderType,
      }
      this.getList()
      this.$emit('table-sort-change', this.searchOrder)
    },
    tableCellChange({ colValue, colProp }) {
      this.tempRow = { ...this.tempRow, [colProp]: colValue }
    },
  }
}
</script>

<style scoped lang="less">
.gjw-table {
  height: 100%;
  /deep/ .el-table  {
    height: 100%;
    .el-table__body-wrapper {
      height: 100% !important;
    }
  }
}
.green{
  color: #67C23A;
}
.red{
  color: #F56C6C;
}
</style>
